import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import SEO from '../components/SEO';
import { 
  Package, Truck, QrCode, MessageSquare, Shield, Globe, Building2, Users,
  MapPin, CreditCard, Calendar, Lock, CheckCircle, Star, FileText, Smartphone
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen">
      <SEO 
        title="MoveMate - Moving Company Management"
        description="Streamline your moving business with AI-powered inventory, QR code tracking, real-time chat, driver tracking and seamless customer communication. Start your free trial today."
        url="https://movemate.info"
      />
      {/* Hero Section */}
      <section className="hero-section text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <nav className="flex justify-between items-center mb-12 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MoveMate" className="h-10" />
              <h1 className="text-2xl font-bold">MoveMate</h1>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="hidden sm:block text-white/80 hover:text-white transition">
                Home
              </Link>
              <Link to="/docs" className="hidden sm:block text-white/80 hover:text-white transition">
                Docs
              </Link>
              <Link to="/pricing" className="hidden sm:block text-white/80 hover:text-white transition">
                {t('navigation.pricing', 'Pricing')}
              </Link>
              <Link to="/faq" className="hidden sm:block text-white/80 hover:text-white transition">
                {t('navigation.faq', 'FAQ')}
              </Link>
              <Link to="/contact" className="hidden sm:block text-white/80 hover:text-white transition">
                {t('navigation.contact', 'Contact')}
              </Link>
              {/* Language Selector - Flag Only */}
              <LanguageSelector variant="flag-only" className="text-white" />
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-primary"
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
              >
                {t('navigation.company_login', 'Company Login')}
              </Button>
              <Button
                className="bg-white text-primary hover:bg-gray-100"
                onClick={() => navigate('/register-company')}
                data-testid="nav-register-btn"
              >
                {t('navigation.start_free_trial', 'Start Free Trial')}
              </Button>
            </div>
          </nav>

          <div className="text-center max-w-4xl mx-auto">
            <p className="text-cyan-200 mb-4 text-sm font-medium tracking-wide uppercase">
              Trusted by 500+ moving companies worldwide
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Moving Company
              <span className="text-cyan-200 block mt-2">Management</span>
            </h1>
            <p className="text-lg sm:text-xl mb-8 text-cyan-50 max-w-2xl mx-auto">
              From estimates to delivery, manage jobs, inventory, customers, and teams in one powerful mobile-first platform
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6 h-auto"
                onClick={() => navigate('/register-company')}
                data-testid="hero-get-started-btn"
              >
                Start Your Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
                onClick={() => navigate('/contact')}
              >
                Watch Demo
              </Button>
            </div>
            <p className="text-cyan-200/80 mt-6 text-sm">
              14-day free trial • No credit card required • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-slate-900">
              Moving Companies Struggle With…
            </h2>
            <p className="text-lg text-slate-600">
              Traditional methods create inefficiencies that cost you time, money, and customer satisfaction
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ProblemCard
              icon={<FileText className="text-red-500" size={28} />}
              title="Scattered Paperwork"
              description="Job details lost across emails, texts, and paper forms leading to missed information"
            />
            <ProblemCard
              icon={<Package className="text-red-500" size={28} />}
              title="Manual Inventory Chaos"
              description="Hand-written lists, lost items, and hours wasted searching storage facilities"
            />
            <ProblemCard
              icon={<MessageSquare className="text-red-500" size={28} />}
              title="Poor Communication"
              description="Customers constantly calling for updates while your team struggles to respond"
            />
            <ProblemCard
              icon={<MapPin className="text-red-500" size={28} />}
              title="Storage Nightmares"
              description="No visibility into which items are in which containers at which facilities"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
              <Building2 className="text-blue-600" size={20} />
              <span className="text-blue-600 font-semibold">Everything You Need in One Platform</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              <span className="gradient-text">Streamline Your Entire Moving Operation</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Eliminate scattered tools and manage your business with comprehensive features built for modern moving companies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={<Truck size={24} />}
              title="Job Management"
              description="Create, track, and manage jobs from quote to completion with real-time team assignment"
            />
            <FeatureCard
              icon={<QrCode size={24} />}
              title="Smart Inventory"
              description="QR code generation and scanning for items with room-by-room tracking and photos"
            />
            <FeatureCard
              icon={<Users size={24} />}
              title="Customer Portal"
              description="Real-time job tracking, digital estimates, approvals, and invoice management"
            />
            <FeatureCard
              icon={<Package size={24} />}
              title="Storage Management"
              description="Track crates, containers, and storage units across multiple facilities"
            />
            <FeatureCard
              icon={<MapPin size={24} />}
              title="Route Planning"
              description="Google Maps integration with real-time driver tracking and optimized routes"
            />
            <FeatureCard
              icon={<CreditCard size={24} />}
              title="Financial Management"
              description="Digital estimates, invoices, Stripe payments, and VAT/tax management"
            />
            <FeatureCard
              icon={<MessageSquare size={24} />}
              title="Team Collaboration"
              description="In-app chat, notifications, SMS/Email alerts with role-based permissions"
            />
            <FeatureCard
              icon={<Shield size={24} />}
              title="White-Label Ready"
              description="Custom branding, domains, and email templates with multi-tenant architecture"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              <span className="gradient-text">How It Works</span>
            </h2>
            <p className="text-lg text-slate-600">
              MoveMate streamlines your workflow from initial quote to final delivery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Step
              number="1"
              title="Create the Job"
              description="Add customer details, addresses, notes, inventory needs, and assign your team"
            />
            <Step
              number="2"
              title="Scan & Track Items"
              description="Use the mobile app to scan items, add photos, generate QR labels, and track containers"
            />
            <Step
              number="3"
              title="Deliver & Manage"
              description="Track moves in real time, notify customers, take photos at delivery, and close jobs digitally"
            />
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-4">
                <Smartphone className="text-green-600" size={20} />
                <span className="text-green-600 font-semibold">Mobile App for Movers</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Everything Your Crew Needs On Their Phones
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Give your crew job details, checklists, customer info, photos, signatures, and real-time updates — right from their phones.
              </p>
              <ul className="space-y-3">
                <FeatureCheck text="View daily jobs and schedules" />
                <FeatureCheck text="Scan and log items with QR codes" />
                <FeatureCheck text="Upload condition photos" />
                <FeatureCheck text="Capture customer signatures" />
                <FeatureCheck text="Real-time location tracking" />
                <FeatureCheck text="Offline mode support" />
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl p-8 flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Smartphone className="w-24 h-24 text-blue-600 mx-auto mb-4" />
                <p className="text-blue-800 font-semibold">iOS & Android App</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-4">
              <Lock className="text-cyan-400" size={20} />
              <span className="text-cyan-400 font-semibold">Enterprise-Grade Security</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Your Data is Protected
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Industry-leading encryption, backups, and access control
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <SecurityFeature
              icon={<Shield size={28} />}
              title="End-to-End Encryption"
              description="All data encrypted in transit (TLS 1.3) and at rest using AES-256"
            />
            <SecurityFeature
              icon={<Calendar size={28} />}
              title="Automated Backups"
              description="Daily encrypted backups stored across multiple secure data centers"
            />
            <SecurityFeature
              icon={<Users size={28} />}
              title="Role-Based Permissions"
              description="Fine-grained access control for admins, managers, drivers, and customers"
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-slate-600">
              Moving companies around the world trust MoveMate
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="MoveMate completely transformed our process. Job handling, storage, and communication are all smoother than ever."
              name="Sarah Thompson"
              role="Operations Manager"
            />
            <TestimonialCard
              quote="The QR inventory system alone saves us hours every week. No more lost items in storage!"
              name="Andrew Miller"
              role="Warehouse Lead"
            />
            <TestimonialCard
              quote="Customers love the real-time updates and portal. We love the reduced call volume."
              name="Lisa Rodriguez"
              role="Customer Service Lead"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="hero-section text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Simplify Your Moving Operations?
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            Join hundreds of moving companies using MoveMate to streamline jobs, storage, communication, and customer satisfaction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-primary hover:bg-gray-100 text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/register-company')}
              data-testid="cta-get-started-btn"
            >
              Start Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
              onClick={() => navigate('/contact')}
            >
              Book a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 text-white mb-4">
                <img src="/logo.png" alt="MoveMate" className="h-8" />
                <span className="font-bold text-xl">MoveMate</span>
              </div>
              <p className="text-sm">
                Complete management system for modern removal companies.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register-company" className="hover:text-white transition">Start Free Trial</Link></li>
                <li><Link to="/login" className="hover:text-white transition">Login</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/docs" className="hover:text-white transition">Documentation</Link></li>
                <li><Link to="/faq" className="hover:text-white transition">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-white transition">Contact Us</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition">Terms & Conditions</Link></li>
                <li><span>Aztrix Media Ltd.</span></li>
                <li><span>United Kingdom</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; 2025 MoveMate. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const ProblemCard = ({ icon, title, description }) => (
  <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold mb-2 text-slate-900">{title}</h3>
    <p className="text-slate-600 text-sm">{description}</p>
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all">
    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold mb-2 text-slate-900">{title}</h3>
    <p className="text-slate-600 text-sm">{description}</p>
  </div>
);

const Step = ({ number, title, description }) => (
  <div className="text-center">
    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
      {number}
    </div>
    <h3 className="text-xl font-semibold mb-2 text-slate-900">{title}</h3>
    <p className="text-slate-600">{description}</p>
  </div>
);

const FeatureCheck = ({ text }) => (
  <li className="flex items-center gap-3">
    <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
    <span className="text-slate-700">{text}</span>
  </li>
);

const SecurityFeature = ({ icon, title, description }) => (
  <div className="text-center">
    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center text-cyan-400 mx-auto mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-slate-400 text-sm">{description}</p>
  </div>
);

const TestimonialCard = ({ quote, name, role }) => (
  <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
    <div className="flex gap-1 mb-4">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
      ))}
    </div>
    <p className="text-slate-700 mb-4 italic">"{quote}"</p>
    <div>
      <p className="font-semibold text-slate-900">{name}</p>
      <p className="text-slate-500 text-sm">{role}</p>
    </div>
  </div>
);

export default LandingPage;
